import { getProviderRevenueAnalytics } from './http/labRevenueAnalytics';
import type { GetProviderRevenueAnalyticsOptions } from './http/labRevenueAnalytics';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import type { RevenueAnalyticsRow } from 'src/admin/LAB/RevenueAnalytics/types';

/** QueryKey constant for provider revenue analytics. */
export const LAB_REVENUE_ANALYTICS = 'lab-revenue-analytics';

/**
 * Query for one page of a single provider's per-symbol revenue analytics.
 *
 * `symbol`, `skip`, and `limit` are part of the key so distinct page /
 * search slices don't clobber each other, and they can be invalidated
 * together via the `[LAB_REVENUE_ANALYTICS, providerId]` prefix. Toggling
 * superuser needs no key dimension: QueryCacheUserService resets
 * user-scoped queries when the effective identity changes. The query
 * function asserts the provider is present; callers gate execution with
 * `enabled: Boolean(provider)` so it never runs without one.
 */
export const getProviderRevenueAnalyticsQuery = (
    provider: Provider | undefined,
    options: GetProviderRevenueAnalyticsOptions = {},
): QueryObserverOptions<RevenueAnalyticsRow[], Error, RevenueAnalyticsRow[]> => {
    const { skip, limit, symbol } = options;
    return {
        queryKey: [LAB_REVENUE_ANALYTICS, provider?.id ?? null, symbol ?? null, skip ?? null, limit ?? null],
        queryFn: () => {
            if (!provider) throw new Error('A provider is required to load revenue analytics.');
            return getProviderRevenueAnalytics(provider, options);
        },
        enabled: Boolean(provider),
    };
};
